import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnGetTableById, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { RowNode } from "ag-grid-community";
import { dateToString } from "@shared/utils/DateUtils";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import SnSwitch, { SnSwitchHandle } from "@shared/components/SnSwitch";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_INV_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const txtBarcodeRef = useRef<any>(null);

  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보
  //#endregion

  // 스위치
  const switchRef = useRef<SnSwitchHandle>(null);
  const [switchLabels, setswitchLabels] = useState({ check: "", unCheck: "" });

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });

        // 컬럼명 = 스위치 라벨
        let checkLabel = "";
        let unCheckLabel = "";
        let pltVisible = "";
        let ctVisible = "";

        rtn.grid.grid1.forEach((item: any) => {
          switch (item.cd_column) {
            case "no_plt":
              checkLabel = item.nm_column;
              pltVisible = item.fg_visible;
              break;
            case "no_ct":
              unCheckLabel = item.nm_column;
              ctVisible = item.fg_visible;
              break;
          }
        });

        setswitchLabels({ check: checkLabel, unCheck: unCheckLabel });

        if (pltVisible === "N") {
          switchRef.current?.setChecked(false);
        }

        if (!(ctVisible === "Y" && pltVisible === "Y")) {
          switchRef.current?.setReadOnly(true);
        }
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    // 품목팝업 시리얼 재고만
    searchRef.current?.applyPopupConditions?.({
      cd_item: { yn_sn: "Y" },
    });
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
    onPrint,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const requiredValuesCheckSearch = searchRef.current.requiredValuesCheck();

    if (!requiredValuesCheckSearch) {
      // 초기화
      setDataSources({
        grid1: [],
        grid2: [],
      });
      return false;
    }

    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_inv_reg_001_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
        return false;
      }

      const focusedRowNode2 = getGridCurrent("grid2").focusedRowNode();
      const barcode = switchRef.current?.getChecked() === true ? "PLT" : "CT";
      const rowCnt = getGridCurrent("grid2").getDisplayedRowCount();
      const cdItem = focusedRowNode2.data.cd_item;
      const today = dateToString(new Date());

      const params = {
        id: "mas_bsn_get",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_bsn: barcode,
          cd_item: cdItem,
          cd_item_cust: "",
          dt: today,
          qt: rowCnt,
          seq: 1,
          yn_save: "Y",
          dc_param_exc: "",
        },
      };
      const data = await fnGetTableById(params);

      const colId =
        switchRef.current?.getChecked() === true ? "no_plt" : "no_ct";
      const noBsn = data[0].no_bsn;

      const result: any[] = [];
      getGridCurrent("grid2").forEachNode((node: RowNode) => {
        node.setDataValue(colId, noBsn, "noEvent");
        result.push({
          ...node.data,
        });
      });

      const updateData = {
        update: result,
        modifiedRowcount: rowCnt,
      };

      // 요청 데이터 구성
      const SaveData = {
        qtio_sn: updateData,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_INV_REG_001_SAVE_01", SaveData);
      if (!ok) return false;
    } catch (error: any) {
      return false;
    }
  };
  //#endregion

  //#region onPrint
  const onPrint = async () => {
    let no_plt = "|";
    let no_ct = "|";

    if (getGridCurrent("grid2").getDisplayedRowCount() > 0) {
      getGridCurrent("grid2").forEachNode((node: RowNode) => {
        const rowData = node.data;
        if (rowData.no_ct !== "" && rowData.no_ct !== null) {
          no_ct += rowData.no_ct + "|";
        }
        if (rowData.no_plt !== "" && rowData.no_plt !== null) {
          no_plt += rowData.no_plt + "|";
        }
      });
    } else {
      return;
    }
    return {
      no_plt: no_plt,
      no_ct: no_ct,
    };
  };
  //#endregion

  //#region Grid1 Events
  const onGrid1BtnsClick = async (id: string) => {
    getGridCurrent("grid1").stopEditing();

    const selectedItem = getGridCurrent("grid1").getCheckedRows();
    if (selectedItem.length < 1) {
      showDialog("alter", "선택된 항목이 없습니다.");
      return false;
    }

    switch (id) {
      case "addRow":
        selectInv("C", "");
        break;
    }
  };
  //#endregion

  //#region Grid2 Events
  const onCellFocused2 = (): any => {
    txtBarcodeRef.current.focus();
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) return;

    switch (id) {
      case "deleteRow":
        const focusedRowNode2 = getGridCurrent("grid2").focusedRowNode();
        getGridCurrent("grid1").forEachNode((node: RowNode) => {
          if (node.data.no_sn === focusedRowNode2.data.no_sn) {
            node.setDataValue("yn_visible", "Y", "noEvent");
          }
        });
        getGridCurrent("grid2").deleteSelectedRows();

        getGridCurrent("grid1").applyFilters({
          yn_visible: "Y",
        });
        break;
    }
  };
  //#endregion

  //#region onBarcodeEnter
  const onBarcodeEnter = async (e: any) => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const barcode = e;

    if (barcode === "" || barcode === null || barcode === undefined) {
      return;
    }

    selectInv("S", barcode);
  };
  //#endregion

  //#region Method
  const selectInv = (id: string, barcode: string) => {
    let selectedItem: any[] = [];
    let selectedSnSet = new Set();

    switch (id) {
      case "C": //선택
        selectedItem = getGridCurrent("grid1").getCheckedRows();
        selectedSnSet = new Set(selectedItem.map((item) => item.no_sn));
        break;
      case "S": // 스캔
        getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
          if (
            node.data.no_sn === barcode ||
            node.data.no_ct === barcode ||
            node.data.no_plt === barcode
          ) {
            selectedItem.push(node.data);
          }
        });
        selectedSnSet = new Set(selectedItem.map((item) => item.no_sn));

        if (selectedSnSet.size < 1) {
          showDialog("alter", "바코드 정보를 찾을 수 없습니다.");
        }
        break;
    }

    selectedItem.forEach((row) => {
      const newRow = {
        cd_firm: row.cd_firm,
        no_io: row.no_io,
        no_line: row.no_line,
        fg_slip: row.fg_slip,
        no_sn: row.no_sn,
        no_ct: row.no_ct,
        no_plt: row.no_plt,
        cd_item: row.cd_item,
      };
      getGridCurrent("grid2").addRow(newRow);
    });

    getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
      if (selectedSnSet.has(node.data.no_sn)) {
        node.setDataValue("yn_visible", "N", "noEvent");
      }
    });

    getGridCurrent("grid1").applyFilters({
      yn_visible: "Y",
    });
    getGridCurrent("grid1").clearCheckBox();
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBtnAddClick: onGrid1BtnsClick,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellFocused: onCellFocused2,
          onBtnDeleteClick: onGrid2BtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 min-w-0 flex flex-row h-full space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="50%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{
              rowSelection: "single",
              title: "재고정보",
              ischeckboxSelection: true,
            }}
            events={grids.grid1.events}
            header={
              <div>
                <SnBarcodeBox
                  onEnter={onBarcodeEnter}
                  inputRef={txtBarcodeRef}
                />
              </div>
            }
          />
        </SnResizeBar>
        <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
          <SnGrid
            ref={gridRefs.current["grid2"]} // Grid Name
            columnConfig={grids.grid2.columns} // 컬럼 설정 전달
            rowData={grids.grid2.dataSource} // Datasource
            etcProps={{
              rowSelection: "single",
              title: "포장|출력정보",
            }}
            events={grids.grid2.events}
            header={
              <div>
                <SnSwitch
                  ref={switchRef}
                  labelCheck={switchLabels.check}
                  labelUncheck={switchLabels.unCheck}
                  defaultChecked={true}
                />
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_INV_REG_001;
